import { useDeferredValue, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type {
  AdminCheckoutRequest,
  AdminInquiry,
  CheckoutRequestPayload,
  InquiryPayload,
  MediaManifestEntry,
  RequestStatus,
} from './lib/cms'
import { localContent, type BlogPost, type CmsData, type Product } from './content/localContent'

type CmsModule = typeof import('./lib/cms')

let cmsModulePromise: Promise<CmsModule> | null = null

function getCmsModule() {
  if (!cmsModulePromise) {
    cmsModulePromise = import('./lib/cms')
  }

  return cmsModulePromise
}

type CartItem = {
  slug: string
  title: string
  price: number | null
  image: string | null
  quantity: number
}

const requestStatuses: RequestStatus[] = ['new', 'reviewing', 'quoted', 'invoiced', 'closed']

type AdminDetailSelection =
  | { type: 'checkout'; id: string }
  | { type: 'inquiry'; id: string }
  | null

type ProductEditorDraft = {
  summary: string
  image: string
  catalogGroup: string
  productFamily: string
  deploymentContexts: string
  hardwareTypes: string
  capabilities: string
}

type SeoStructuredData = Record<string, unknown> | Array<Record<string, unknown>>

const DEFAULT_SITE_ORIGIN = 'https://digitalsignagegear.com'
const DEFAULT_SOCIAL_IMAGE_PATH = '/wordpress-media/083-about-us.jpg'
const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
const HOMEPAGE_VIDEO_EMBED_URL = 'https://www.youtube-nocookie.com/embed/oEiX9SFJJ8I?rel=0&modestbranding=1'
const HOMEPAGE_VIDEO_WATCH_URL = 'https://youtu.be/oEiX9SFJJ8I?si=BjwVlyF9n3uFWGrc'
const HOMEPAGE_VIDEO_POSTER = '/wordpress-media/083-about-us-741x324.jpg'
const BRAND_NAME = 'Digital Signage Gear'
const HERO_MEDIA_VIDEO_MAP: Record<string, string> = {
  '/wordpress-media/076-Kit-animation-1.gif': '/wordpress-media/066-Kit-animation-1.mp4',
}

function resolveHeroMediaPath(path: string) {
  return HERO_MEDIA_VIDEO_MAP[path] ?? path
}

function isVideoPath(path: string) {
  return /\.(mp4|webm|ogg)$/i.test(path)
}

function getSiteOrigin() {
  const configuredOrigin = import.meta.env.VITE_SITE_URL?.trim()

  if (configuredOrigin) {
    return configuredOrigin.replace(/\/$/, '')
  }

  return DEFAULT_SITE_ORIGIN
}

function toAbsoluteUrl(value: string, siteOrigin = getSiteOrigin()) {
  if (/^https?:\/\//i.test(value)) {
    return value
  }

  return new URL(value.startsWith('/') ? value : `/${value}`, `${siteOrigin}/`).toString()
}

function formatDocumentTitle(title: string) {
  return title.toLowerCase().includes(BRAND_NAME.toLowerCase()) ? title : `${title} | ${BRAND_NAME}`
}

function buildBreadcrumbStructuredData(items: Array<{ name: string; url: string }>) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

function upsertMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function upsertLinkTag(rel: string, href: string) {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}

function upsertStructuredData(structuredData: SeoStructuredData | null) {
  const existing = document.head.querySelector('#seo-structured-data')

  if (!structuredData) {
    existing?.remove()
    return
  }

  const payload = Array.isArray(structuredData)
    ? { '@context': 'https://schema.org', '@graph': structuredData }
    : { '@context': 'https://schema.org', ...structuredData }

  let script = existing as HTMLScriptElement | null

  if (!script) {
    script = document.createElement('script')
    script.id = 'seo-structured-data'
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }

  script.textContent = JSON.stringify(payload)
}

function SeoHead({
  title,
  description,
  canonicalPath,
  image = DEFAULT_SOCIAL_IMAGE_PATH,
  type = 'website',
  robots = DEFAULT_ROBOTS,
  structuredData = null,
}: {
  title: string
  description: string
  canonicalPath?: string
  image?: string | null
  type?: 'website' | 'article'
  robots?: string
  structuredData?: SeoStructuredData | null
}) {
  const location = useLocation()

  useEffect(() => {
    const siteOrigin = getSiteOrigin()
    const canonicalUrl = toAbsoluteUrl(canonicalPath ?? location.pathname, siteOrigin)
    const socialImageUrl = toAbsoluteUrl(image ?? DEFAULT_SOCIAL_IMAGE_PATH, siteOrigin)

    document.title = formatDocumentTitle(title)
    upsertMetaTag('name', 'description', description)
    upsertMetaTag('name', 'robots', robots)
    upsertMetaTag('property', 'og:locale', 'en_US')
    upsertMetaTag('property', 'og:site_name', 'Digital Signage Gear')
    upsertMetaTag('property', 'og:type', type)
    upsertMetaTag('property', 'og:title', title)
    upsertMetaTag('property', 'og:description', description)
    upsertMetaTag('property', 'og:url', canonicalUrl)
    upsertMetaTag('property', 'og:image', socialImageUrl)
    upsertMetaTag('property', 'og:image:alt', title)
    upsertMetaTag('name', 'twitter:card', 'summary_large_image')
    upsertMetaTag('name', 'twitter:title', title)
    upsertMetaTag('name', 'twitter:description', description)
    upsertMetaTag('name', 'twitter:image', socialImageUrl)
    upsertLinkTag('canonical', canonicalUrl)
    upsertStructuredData(structuredData)
  }, [canonicalPath, description, image, location.pathname, robots, structuredData, title, type])

  return null
}

function uniqueStrings(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))]
}

function parseEditorList(value: string) {
  return uniqueStrings(value.split(','))
}

function createProductEditorDraft(product: Product): ProductEditorDraft {
  return {
    summary: product.summary,
    image: product.image ?? '',
    catalogGroup: product.taxonomy.catalogGroup,
    productFamily: product.taxonomy.productFamily,
    deploymentContexts: product.taxonomy.deploymentContexts.join(', '),
    hardwareTypes: product.taxonomy.hardwareTypes.join(', '),
    capabilities: product.taxonomy.capabilities.join(', '),
  }
}

function App() {
  const location = useLocation()
  const hasHydratedCmsRef = useRef(false)
  const [cmsData, setCmsData] = useState<CmsData>(localContent)
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const stored = window.localStorage.getItem('dsg-cart')

    if (!stored) {
      return []
    }

    try {
      return JSON.parse(stored) as CartItem[]
    } catch {
      return []
    }
  })

  useEffect(() => {
    window.localStorage.setItem('dsg-cart', JSON.stringify(cartItems))
  }, [cartItems])

  useEffect(() => {
    let cancelled = false

    const hydrateCmsData = () => {
      if (hasHydratedCmsRef.current) {
        return
      }

      hasHydratedCmsRef.current = true

      getCmsModule().then((cms) => cms.loadCmsData()).then((result) => {
        if (!cancelled) {
          setCmsData(result.data)
        }
      }).catch(() => undefined)
    }

    if (location.pathname.startsWith('/admin')) {
      hydrateCmsData()

      return () => {
        cancelled = true
      }
    }

    const isLikelyCrawler = /bot|crawler|spider|slurp|bingpreview|adsbot|googleother/i.test(
      navigator.userAgent,
    )

    if (isLikelyCrawler) {
      hydrateCmsData()

      return () => {
        cancelled = true
      }
    }

    const handleFirstInteraction = () => {
      hydrateCmsData()
      window.removeEventListener('pointerdown', handleFirstInteraction)
      window.removeEventListener('keydown', handleFirstInteraction)
      window.removeEventListener('touchstart', handleFirstInteraction)
    }

    const passiveHydrationTimer = window.setTimeout(() => {
      hydrateCmsData()
    }, 2500)

    window.addEventListener('pointerdown', handleFirstInteraction, { passive: true })
    window.addEventListener('keydown', handleFirstInteraction)
    window.addEventListener('touchstart', handleFirstInteraction, { passive: true })

    return () => {
      cancelled = true
      window.clearTimeout(passiveHydrationTimer)
      window.removeEventListener('pointerdown', handleFirstInteraction)
      window.removeEventListener('keydown', handleFirstInteraction)
      window.removeEventListener('touchstart', handleFirstInteraction)
    }
  }, [location.pathname])

  async function refreshData() {
    const cms = await getCmsModule()
    const result = await cms.loadCmsData()
    setCmsData(result.data)
  }

  async function submitInquiry(payload: InquiryPayload) {
    const cms = await getCmsModule()
    return cms.saveInquiry(payload)
  }

  async function submitCheckoutRequest(payload: CheckoutRequestPayload) {
    const cms = await getCmsModule()
    return cms.saveCheckoutRequest(payload)
  }

  function addToCart(product: Product, quantity: number) {
    setCartItems((current) => {
      const existing = current.find((item) => item.slug === product.slug)

      if (existing) {
        return current.map((item) =>
          item.slug === product.slug
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        )
      }

      return [
        ...current,
        {
          slug: product.slug,
          title: product.title,
          price: product.price,
          image: product.image,
          quantity,
        },
      ]
    })
  }

  function updateCartQuantity(slug: string, quantity: number) {
    setCartItems((current) =>
      current
        .map((item) => (item.slug === slug ? { ...item, quantity } : item))
        .filter((item) => item.quantity > 0),
    )
  }

  function removeFromCart(slug: string) {
    setCartItems((current) => current.filter((item) => item.slug !== slug))
  }

  return (
    <SiteShell
      settings={cmsData.settings}
      products={cmsData.products}
      industries={cmsData.homepage.industries.map((industry) => industry.name)}
      cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
    >
      <Routes>
        <Route
          path="/"
          element={<HomePage data={cmsData} />}
        />
        <Route path="/shop" element={<ShopPage products={cmsData.products} />} />
        <Route
          path="/shop/:slug"
          element={<ProductDetailPage products={cmsData.products} onAddToCart={addToCart} />}
        />
        <Route path="/about" element={<AboutPage data={cmsData} />} />
        <Route
          path="/faq"
          element={<FaqPage data={cmsData} title={cmsData.pages.faq.title} />}
        />
        <Route path="/blog" element={<BlogPage posts={cmsData.posts} />} />
        <Route
          path="/blog/:slug"
          element={<BlogPostPage posts={cmsData.posts} settingsName={cmsData.settings.brandName} />}
        />
        <Route
          path="/contact"
          element={<ContactPage data={cmsData} onSubmitInquiry={submitInquiry} />}
        />
        <Route
          path="/admin"
          element={<AdminPage data={cmsData} onRefresh={refreshData} />}
        />
        <Route
          path="/cart"
          element={
            <CartPage
              items={cartItems}
              onUpdateQuantity={updateCartQuantity}
              onRemove={removeFromCart}
            />
          }
        />
        <Route
          path="/checkout"
          element={
            <CheckoutPage
              items={cartItems}
              onSubmitRequest={submitCheckoutRequest}
              onClearCart={() => setCartItems([])}
            />
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </SiteShell>
  )
}

function SiteShell({
  children,
  settings,
  products,
  industries,
  cartCount,
}: {
  children: ReactNode
  settings: CmsData['settings']
  products: Product[]
  industries: string[]
  cartCount: number
}) {
  const location = useLocation()
  const featuredIndustries = industries.slice(0, 5)
  const primaryCatalogGroups = [...new Set(products.map((product) => product.taxonomy.catalogGroup))]
  const accessoryFamilies = [
    ...new Set(
      products
        .filter((product) => product.taxonomy.catalogGroup === 'Accessories')
        .map((product) => product.taxonomy.productFamily),
    ),
  ]
  const compactAccessoryFamilies = accessoryFamilies.slice(0, 4)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="brand-lockup">
          <NavLink to="/" className="brand-mark" aria-label={settings.brandName}>
            <img
              className="brand-logo"
              src={settings.logoPath}
              alt="Digital Signage Gear"
                width={385}
                height={131}
            />
            <span className="brand-text-group">
              <span className="brand-title">Digital Signage Gear</span>
              <span className="brand-tagline">{settings.tagline}</span>
            </span>
          </NavLink>
        </div>
        <nav className="site-nav" aria-label="Primary">
          <NavLink to="/shop">Shop</NavLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/blog">Blog</NavLink>
          <NavLink to="/faq">FAQ</NavLink>
          <NavLink to="/contact">Contact</NavLink>
          <NavLink to="/admin">Admin</NavLink>
          <NavLink to="/cart">Cart ({cartCount})</NavLink>
        </nav>
      </header>
      <main>{children}</main>
      <footer className="site-footer">
        <div className="footer-brand-column">
          <img
            className="footer-logo"
            src={settings.logoLightPath}
            alt="Digital Signage Gear"
            width={385}
            height={131}
          />
          <p className="footer-summary">
            Explore screens, mounts, accessories, and complete signage kits built for
            straightforward rollout.
          </p>
          <div className="footer-cta-inline">
            <NavLink to="/checkout?mode=quote" className="button button-primary">
              Request a Quote
            </NavLink>
            <a className="footer-inline-link" href={`mailto:${settings.primaryEmail}`}>
              {settings.primaryEmail}
            </a>
          </div>
        </div>
        <div>
          <p className="footer-heading">Browse</p>
          <nav className="footer-link-list" aria-label="Footer navigation">
            <NavLink to="/shop">Shop</NavLink>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/blog">Blog</NavLink>
            <NavLink to="/faq">FAQ</NavLink>
          </nav>
        </div>
        <div>
          <p className="footer-heading">Category Groups</p>
          <nav className="footer-link-list footer-category-stack" aria-label="Shop by category groups">
            {primaryCatalogGroups.map((category) => (
              <NavLink key={category} to="/shop">
                {category}
              </NavLink>
            ))}
            {compactAccessoryFamilies.length ? <span className="footer-mini-label">Top Accessory Types</span> : null}
            {compactAccessoryFamilies.length ? (
              <div className="footer-compact-links">
                {compactAccessoryFamilies.map((category) => (
                  <NavLink key={category} to="/shop">
                    {category}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </nav>
        </div>
        <div>
          <p className="footer-heading">Shop by Industry</p>
          <nav className="footer-link-list" aria-label="Shop by industry">
            {featuredIndustries.map((industry) => (
              <NavLink key={industry} to="/shop">
                {industry}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="footer-bottom-row">
          <p>© {new Date().getFullYear()} Digital Signage Gear</p>
          <div className="footer-bottom-links">
            <NavLink to="/checkout?mode=quote">Quote</NavLink>
            <NavLink to="/contact">Support</NavLink>
            <NavLink to="/admin">Admin</NavLink>
            <NavLink to="/cart">Cart ({cartCount})</NavLink>
          </div>
        </div>
      </footer>
    </div>
  )
}

function HomePage({
  data,
}: {
  data: CmsData
}) {
  const [isVideoEmbedActive, setIsVideoEmbedActive] = useState(false)
  const heroMediaPath = resolveHeroMediaPath(data.homepage.heroMediaPath)

  return (
    <>
      <SeoHead
        title="Digital Signage Kits, Displays, and Mounting Hardware"
        description={data.homepage.summary}
        canonicalPath="/"
        image={data.homepage.industries[0]?.imagePath ?? data.settings.logoPath}
        structuredData={[
          {
            '@type': 'Organization',
            name: data.settings.brandName,
            url: getSiteOrigin(),
            logo: toAbsoluteUrl(data.settings.logoPath),
            email: data.settings.primaryEmail,
          },
          {
            '@type': 'WebSite',
            name: data.settings.brandName,
            url: getSiteOrigin(),
            description: data.homepage.summary,
          },
          buildBreadcrumbStructuredData([{ name: 'Home', url: getSiteOrigin() }]),
        ]}
      />
      <section className="hero-panel">
        <div className="hero-copy">
          <p className="eyebrow">{data.homepage.eyebrow}</p>
          <h1>{data.homepage.title}</h1>
          <p className="lede">{data.homepage.summary}</p>
          <div className="hero-actions">
            <NavLink to="/shop" className="button button-primary">
              Browse Kits
            </NavLink>
            <NavLink to="/contact" className="button button-secondary">
              Talk Through a Deployment
            </NavLink>
          </div>
          <p className="status-note">
            Browse the current catalog and featured deployment kits.
          </p>
        </div>
        <div className="hero-visual" aria-label="Digital Signage Gear overview">
          <div className="hero-screen">
            <div className="hero-video-shell">
              {isVideoEmbedActive ? (
                <iframe
                  className="hero-video"
                  src={HOMEPAGE_VIDEO_EMBED_URL}
                  title="Digital Signage Gear homepage video"
                  loading="lazy"
                  width={560}
                  height={315}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  className="hero-video-launch"
                  onClick={() => setIsVideoEmbedActive(true)}
                  aria-label="Load and play the Digital Signage Gear YouTube video"
                >
                  <img
                    className="hero-video-poster"
                    src={HOMEPAGE_VIDEO_POSTER}
                    alt="Preview of Digital Signage Gear video"
                    width={741}
                    height={324}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="hero-video-play">Play Video</span>
                </button>
              )}
            </div>
            <div className="hero-video-meta">
              <span>See a live overview of the platform and hardware approach.</span>
              <a href={HOMEPAGE_VIDEO_WATCH_URL} target="_blank" rel="noreferrer">
                Open on YouTube
              </a>
            </div>
          </div>
          <div className="hero-card-grid">
            {data.homepage.metrics.map((metric) => (
              <article key={metric.label} className="metric-card">
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block intro-grid">
        <article className="feature-panel">
          <p className="eyebrow">About Us</p>
          <h2>Affordable kits built for self-installation.</h2>
          <p>
            We’re your go-to source for digital signage gear that small and medium-sized
            businesses can deploy without the usual AV friction.
          </p>
          <NavLink to="/about">Learn more</NavLink>
        </article>
        <article className="feature-panel">
          <p className="eyebrow">Our Blog</p>
          <h2>Practical guidance for buyers and deployers.</h2>
          <p>
            Use articles and buying guides to compare screens, understand deployment tradeoffs,
            and choose the right equipment for your rollout.
          </p>
          <NavLink to="/blog">View posts</NavLink>
        </article>
        <article className="feature-panel feature-panel-cta">
          <p className="eyebrow">Need Help?</p>
          <h2>Tell us what you’re looking for.</h2>
          <p>
            If you don’t see the right configuration yet, use the contact form and we’ll
            narrow the best fit for your industry.
          </p>
          <NavLink to="/contact">Contact us</NavLink>
        </article>
      </section>

      <section className="section-block motion-callout">
        <div className="motion-callout-copy">
          <p className="eyebrow">Digital Signage Made Easy</p>
          <h2>Delivery-ready kits that are easier to choose and easier to roll out.</h2>
          <p>
            Start with the hardware you actually need, skip the usual AV complexity, and move
            from product selection to quote or checkout handoff without slowing down the rollout.
          </p>
          <div className="motion-callout-actions">
            <NavLink to="/shop" className="button button-primary">
              Shop Easy-to-Deploy Kits
            </NavLink>
            <NavLink to="/checkout?mode=quote" className="button button-secondary">
              Request a Quote
            </NavLink>
          </div>
        </div>
        <div className="motion-callout-media">
          {isVideoPath(heroMediaPath) ? (
            <video
              className="hero-media"
              src={heroMediaPath}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              width={1840}
              height={685}
              aria-label="Cargo van dropping a Digital Signage Gear box"
            />
          ) : (
            <img
              className="hero-media"
              src={heroMediaPath}
              alt="Cargo van dropping a Digital Signage Gear box"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              width={1840}
              height={685}
            />
          )}
        </div>
      </section>

      <section className="section-block industry-section">
        <div className="section-heading align-left industry-section-heading">
          <p className="eyebrow">Industries</p>
          <h2>Browse kits by deployment context.</h2>
        </div>
        <div className="industry-grid">
          {data.homepage.industries.map((industry) => (
            <NavLink
              key={industry.name}
              to="/shop"
              className="industry-card-link"
            >
              <article className="industry-card interactive">
                <img
                  className="industry-image"
                  src={industry.imagePath}
                  alt={`${industry.name} digital signage deployment example`}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                    width={385}
                    height={240}
                />
                <h3>{industry.name}</h3>
                <p>{industry.description}</p>
                <span className="industry-card-cta">Browse {industry.name} kits</span>
              </article>
            </NavLink>
          ))}
        </div>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <p className="eyebrow">Featured Kits</p>
          <h2>Explore the catalog.</h2>
        </div>
        <div className="product-grid">
          {data.products.slice(0, 6).map((product) => (
            <ProductCard key={product.slug} product={product} compact />
          ))}
        </div>
      </section>

      <section className="split-panel">
        <div>
          <p className="eyebrow">How it works</p>
          <h2>{data.homepage.processTitle}</h2>
        </div>
        <ol className="process-list">
          {data.homepage.steps.map((step) => (
            <li key={step.title}>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <p className="eyebrow">Latest blogs</p>
          <h2>Latest blogs</h2>
        </div>
        <div className="blog-grid">
          {data.posts.slice(0, 3).map((post) => (
            <article key={post.slug} className="blog-card">
              <p className="meta-row">{formatDate(post.publishedAt)}</p>
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
              <NavLink to={`/blog/${post.slug}`}>Read article</NavLink>
            </article>
          ))}
        </div>
      </section>

      <section className="cta-panel">
        <p className="eyebrow">Don’t see what you’re looking for?</p>
        <h2>{data.homepage.ctaTitle}</h2>
        <p>{data.homepage.ctaBody}</p>
        <NavLink to="/contact" className="button button-primary">
          Contact Digital Signage Gear
        </NavLink>
      </section>
    </>
  )
}

function ShopPage({ products }: { products: Product[] }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const categories = ['All', ...new Set(products.flatMap((product) => product.categories))]
  const catalogGroups = ['All', ...new Set(products.map((product) => product.taxonomy.catalogGroup))]
  const requestedCategory = searchParams.get('category') ?? 'All'
  const requestedGroup = searchParams.get('group') ?? 'All'
  const selectedGroup = catalogGroups.includes(requestedGroup) ? requestedGroup : 'All'
  const availableFamilies = [
    'All',
    ...new Set(
      products
        .filter((product) => selectedGroup === 'All' || product.taxonomy.catalogGroup === selectedGroup)
        .map((product) => product.taxonomy.productFamily),
    ),
  ]
  const requestedFamily = searchParams.get('family') ?? 'All'
  const selectedFamily = availableFamilies.includes(requestedFamily) ? requestedFamily : 'All'
  const selectedCategory = categories.includes(requestedCategory) ? requestedCategory : 'All'
  const deferredQuery = useDeferredValue(query)
  const normalizedQuery = deferredQuery.trim().toLowerCase()
  const taxonomyHighlights = catalogGroups
    .filter((group) => group !== 'All')
    .map((group) => {
      const groupProducts = products.filter((product) => product.taxonomy.catalogGroup === group)

      return {
        group,
        count: groupProducts.length,
        families: [...new Set(groupProducts.map((product) => product.taxonomy.productFamily))].slice(
          0,
          4,
        ),
      }
    })

  function applyGroupFilter(group: string) {
    const nextParams = new URLSearchParams(searchParams)

    if (group === 'All') {
      nextParams.delete('group')
      nextParams.delete('family')
    } else {
      nextParams.set('group', group)
      nextParams.delete('family')
    }

    setSearchParams(nextParams)
  }

  function applyFamilyFilter(family: string) {
    const nextParams = new URLSearchParams(searchParams)

    if (family === 'All') {
      nextParams.delete('family')
    } else {
      nextParams.set('family', family)
    }

    setSearchParams(nextParams)
  }

  function applyCategoryFilter(category: string) {
    const nextParams = new URLSearchParams(searchParams)

    if (category === 'All') {
      nextParams.delete('category')
    } else {
      nextParams.set('category', category)
    }

    setSearchParams(nextParams)
  }

  function clearFilters() {
    setSearchParams(new URLSearchParams())
    setQuery('')
    setMobileFiltersOpen(false)
  }

  function renderDropdownFilters(containerClassName: string) {
    return (
      <div className={containerClassName}>
        <label className="shop-filter-field">
          <span className="filter-label">Catalog Group</span>
          <select value={selectedGroup} onChange={(event) => applyGroupFilter(event.target.value)}>
            {catalogGroups.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>
        </label>
        <label className="shop-filter-field">
          <span className="filter-label">Product Family</span>
          <select value={selectedFamily} onChange={(event) => applyFamilyFilter(event.target.value)}>
            {availableFamilies.map((family) => (
              <option key={family} value={family}>
                {family}
              </option>
            ))}
          </select>
        </label>
        <label className="shop-filter-field">
          <span className="filter-label">Category or Context</span>
          <select value={selectedCategory} onChange={(event) => applyCategoryFilter(event.target.value)}>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </label>
      </div>
    )
  }

  const filteredProducts = products.filter((product) => {
    const matchesGroup =
      selectedGroup === 'All' || product.taxonomy.catalogGroup === selectedGroup
    const matchesFamily =
      selectedFamily === 'All' || product.taxonomy.productFamily === selectedFamily
    const matchesCategory =
      selectedCategory === 'All' || product.categories.includes(selectedCategory)
    const haystack = `${product.title} ${product.summary} ${product.categories.join(' ')} ${product.taxonomy.catalogGroup} ${product.taxonomy.productFamily} ${product.taxonomy.deploymentContexts.join(' ')} ${product.taxonomy.capabilities.join(' ')}`.toLowerCase()

    return (
      matchesGroup &&
      matchesFamily &&
      matchesCategory &&
      (!normalizedQuery || haystack.includes(normalizedQuery))
    )
  })
  const activeFilters = [selectedGroup, selectedFamily, selectedCategory].filter((value) => value !== 'All')
  const shopTitle = activeFilters.length
    ? `${activeFilters.join(' / ')} Digital Signage Products`
    : 'Shop Digital Signage Kits, Displays, and Accessories'
  const shopDescription = activeFilters.length
    ? `Browse ${filteredProducts.length} digital signage products for ${activeFilters.join(', ')}.`
    : 'Browse commercial displays, kiosks, mounts, media players, and deployment-ready digital signage accessories.'

  return (
    <section className="page-shell">
      <SeoHead
        title={shopTitle}
        description={shopDescription}
        canonicalPath="/shop"
        image={filteredProducts[0]?.image ?? products[0]?.image}
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'Shop', url: toAbsoluteUrl('/shop') },
          ]),
        ]}
      />
      <div className="section-heading align-left">
        <p className="eyebrow">Shop</p>
        <h1>Deployment-ready signage kits.</h1>
        <p>
          Browse screens, accessories, and industry-specific kit configurations from the
          Digital Signage Gear catalog.
        </p>
      </div>
      <div className="shop-filter-header">
        <div className="shop-filter-header-topline">
          <div>
            <p className="filter-label">Browse Controls</p>
            <h2>Refine the catalog from one header.</h2>
          </div>
          <div className="shop-filter-actions">
            <button
              type="button"
              className="button button-secondary shop-mobile-filter-toggle"
              onClick={() => setMobileFiltersOpen((current) => !current)}
              aria-expanded={mobileFiltersOpen}
              aria-controls="shop-mobile-filters"
            >
              {mobileFiltersOpen ? 'Hide filters' : 'More filters'}
            </button>
            <button type="button" className="button button-secondary" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        </div>
        <div className="shop-filter-grid shop-filter-grid-desktop">
          <label className="shop-filter-field shop-filter-search">
            <span className="filter-label">Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search products, industries, or capabilities"
              aria-label="Search products"
            />
          </label>
          {renderDropdownFilters('shop-filter-dropdowns')}
        </div>
        <div className="shop-filter-mobile-search">
          <label className="shop-filter-field shop-filter-search">
            <span className="filter-label">Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search products, industries, or capabilities"
              aria-label="Search products"
            />
          </label>
        </div>
        {mobileFiltersOpen ? (
          <div id="shop-mobile-filters" className="shop-mobile-filters-panel">
            {renderDropdownFilters('shop-filter-grid shop-filter-grid-mobile')}
          </div>
        ) : null}
        <div className="shop-filter-active-row">
          {selectedGroup !== 'All' ? <span className="chip chip-active">{selectedGroup}</span> : null}
          {selectedFamily !== 'All' ? <span className="chip chip-active">{selectedFamily}</span> : null}
          {selectedCategory !== 'All' ? <span className="chip chip-active">{selectedCategory}</span> : null}
          {normalizedQuery ? <span className="chip chip-active">Search: {deferredQuery}</span> : null}
          {!normalizedQuery && selectedGroup === 'All' && selectedFamily === 'All' && selectedCategory === 'All' ? (
            <span className="status-note">All products currently visible.</span>
          ) : null}
        </div>
      </div>
      <details className="shop-taxonomy-disclosure">
        <summary>
          <span>Browse by catalog group</span>
          <span>{taxonomyHighlights.length} groups</span>
        </summary>
        <div className="shop-taxonomy-grid">
          {taxonomyHighlights.map((entry) => (
            <article key={entry.group} className="shop-taxonomy-card">
              <p className="eyebrow">Catalog Group</p>
              <h2>{entry.group}</h2>
              <p>
                {entry.count} products across {entry.families.length} primary families.
              </p>
              <p className="taxonomy-family-list">{entry.families.join(' · ')}</p>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => applyGroupFilter(entry.group)}
              >
                Browse {entry.group}
              </button>
            </article>
          ))}
        </div>
      </details>
      <div className="shop-results-bar">
        <p>
          Showing {filteredProducts.length} products
          {selectedGroup !== 'All' ? ` in ${selectedGroup}` : ''}
          {selectedFamily !== 'All' ? ` / ${selectedFamily}` : ''}
          {selectedCategory !== 'All' ? ` / ${selectedCategory}` : ''}
        </p>
      </div>
      <div className="product-grid">
        {filteredProducts.map((product) => (
          <ProductCard key={product.slug} product={product} />
        ))}
      </div>
    </section>
  )
}

function ProductCard({
  product,
  compact = false,
}: {
  product: Product
  compact?: boolean
}) {
  return (
    <NavLink to={`/shop/${product.slug}`} className="product-card-link">
      <article className={compact ? 'product-card compact' : 'product-card'}>
        {product.image ? (
          <img className="product-image" src={product.image} alt={product.title} loading="lazy" decoding="async" />
        ) : null}
        <p className="meta-row">{[product.taxonomy.catalogGroup, product.taxonomy.productFamily].filter(Boolean).join(' · ') || 'Digital signage catalog item'}</p>
        <h3>{product.title}</h3>
        <p>{product.summary}</p>
        <div className="product-footer">
          <strong>{product.price ? `$${product.price.toFixed(2)}` : 'Quote on request'}</strong>
          <span>{product.attributes.size ?? product.taxonomy.deploymentContexts[0] ?? 'View details'}</span>
        </div>
      </article>
    </NavLink>
  )
}

function ProductDetailPage({
  products,
  onAddToCart,
}: {
  products: Product[]
  onAddToCart: (product: Product, quantity: number) => void
}) {
  const { slug } = useParams()
  const [quantity, setQuantity] = useState(1)
  const [status, setStatus] = useState('')
  const product = products.find((entry) => entry.slug === slug)

  if (!product) {
    return <NotFoundPage />
  }

  function handleAddToCart() {
    if (!product) {
      return
    }

    onAddToCart(product, quantity)
    setStatus(`${product.title} added to cart.`)
  }

  return (
    <section className="page-shell product-detail-shell">
      <SeoHead
        title={product.title}
        description={product.summary}
        canonicalPath={`/shop/${product.slug}`}
        image={product.image}
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'Shop', url: toAbsoluteUrl('/shop') },
            { name: product.title, url: toAbsoluteUrl(`/shop/${product.slug}`) },
          ]),
          {
            '@type': 'Product',
            name: product.title,
            description: product.summary,
            image: product.image ? [toAbsoluteUrl(product.image)] : undefined,
            sku: product.slug,
            category: product.taxonomy.productFamily,
            brand: {
              '@type': 'Brand',
              name: 'Digital Signage Gear',
            },
            offers: product.price
              ? {
                  '@type': 'Offer',
                  priceCurrency: 'USD',
                  price: product.price.toFixed(2),
                  availability: 'https://schema.org/InStock',
                  url: toAbsoluteUrl(`/shop/${product.slug}`),
                }
              : undefined,
          },
        ]}
      />
      <div className="product-detail-grid">
        <div className="product-detail-media">
          {product.image ? (
            <img className="product-detail-image" src={product.image} alt={product.title} loading="lazy" decoding="async" />
          ) : (
            <div className="product-detail-placeholder">Product image coming soon</div>
          )}
        </div>
        <div className="product-detail-copy">
          <p className="eyebrow">{[product.taxonomy.catalogGroup, product.taxonomy.productFamily, product.taxonomy.deploymentContexts[0]].filter(Boolean).join(' · ') || 'Digital signage catalog item'}</p>
          <h1>{product.title}</h1>
          <p className="product-price">
            {product.price ? `$${product.price.toFixed(2)}` : 'Request pricing'}
          </p>
          <p>{product.summary}</p>
          {product.descriptionHtml ? (
            <div
              className="rich-copy product-description"
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
            />
          ) : null}
          <div className="cart-controls">
            <label>
              Quantity
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
              />
            </label>
            <button type="button" className="button button-primary" onClick={handleAddToCart}>
              Add to Cart
            </button>
            <NavLink to="/checkout?mode=quote" className="button button-secondary">
              Request a Quote
            </NavLink>
          </div>
          {status ? <p className="status-note">{status}</p> : null}
        </div>
      </div>
    </section>
  )
}

function AboutPage({ data }: { data: CmsData }) {
  return (
    <section className="page-shell rich-copy">
      <SeoHead
        title="About Digital Signage Gear"
        description={data.pages.about.summary}
        canonicalPath="/about"
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'About', url: toAbsoluteUrl('/about') },
          ]),
        ]}
      />
      <div className="section-heading align-left">
        <p className="eyebrow">About</p>
        <h1>{data.pages.about.title}</h1>
        <p>{data.pages.about.summary}</p>
      </div>
      <div dangerouslySetInnerHTML={{ __html: data.pages.about.html }} />
    </section>
  )
}

function FaqPage({ data, title }: { data: CmsData; title: string }) {
  return (
    <section className="page-shell">
      <SeoHead
        title="FAQ | Digital Signage Gear"
        description={data.pages.faq.summary}
        canonicalPath="/faq"
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'FAQ', url: toAbsoluteUrl('/faq') },
          ]),
          {
            '@type': 'FAQPage',
            mainEntity: data.faqs.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          },
        ]}
      />
      <div className="section-heading align-left">
        <p className="eyebrow">FAQ</p>
        <h1>{title}</h1>
        <p>{data.pages.faq.summary}</p>
      </div>
      <div className="faq-list">
        {data.faqs.map((item) => (
          <details key={item.id} className="faq-item">
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

function BlogPage({ posts }: { posts: BlogPost[] }) {
  return (
    <section className="page-shell">
      <SeoHead
        title="Blog | Digital Signage Gear"
        description="Guides, deployment advice, and buyer education for digital signage hardware and rollout planning."
        canonicalPath="/blog"
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'Blog', url: toAbsoluteUrl('/blog') },
          ]),
        ]}
      />
      <div className="section-heading align-left">
        <p className="eyebrow">Blog</p>
        <h1>Guides, updates, and buyer education.</h1>
        <p>Use the blog to compare hardware choices and learn how to deploy signage more effectively.</p>
      </div>
      <div className="blog-grid long-form-grid">
        {posts.map((post) => (
          <article key={post.slug} className="blog-card blog-card-wide">
            <p className="meta-row">{formatDate(post.publishedAt)}</p>
            <h2>{post.title}</h2>
            <p>{post.excerpt}</p>
            <NavLink to={`/blog/${post.slug}`}>Open post</NavLink>
          </article>
        ))}
      </div>
    </section>
  )
}

function BlogPostPage({
  posts,
  settingsName,
}: {
  posts: BlogPost[]
  settingsName: string
}) {
  const { slug } = useParams()
  const post = posts.find((entry) => entry.slug === slug)

  if (!post) {
    return <NotFoundPage />
  }

  return (
    <article className="page-shell rich-copy">
      <SeoHead
        title={post.title}
        description={post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
        type="article"
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'Blog', url: toAbsoluteUrl('/blog') },
            { name: post.title, url: toAbsoluteUrl(`/blog/${post.slug}`) },
          ]),
          {
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            datePublished: new Date(post.publishedAt).toISOString(),
            author: {
              '@type': 'Person',
              name: post.author || settingsName,
            },
            publisher: {
              '@type': 'Organization',
              name: settingsName,
            },
            url: toAbsoluteUrl(`/blog/${post.slug}`),
          },
        ]}
      />
      <p className="eyebrow">{settingsName} blog</p>
      <h1>{post.title}</h1>
      <p className="meta-row">{formatDate(post.publishedAt)}</p>
      <div dangerouslySetInnerHTML={{ __html: post.html }} />
    </article>
  )
}

function ContactPage({
  data,
  onSubmitInquiry,
}: {
  data: CmsData
  onSubmitInquiry: (payload: InquiryPayload) => Promise<{ storedInFirestore: boolean }>
}) {
  const [status, setStatus] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setStatus('')

    const formData = new FormData(event.currentTarget)
    const payload: InquiryPayload = {
      name: String(formData.get('name') ?? ''),
      email: String(formData.get('email') ?? ''),
      subject: String(formData.get('subject') ?? ''),
      industry: String(formData.get('industry') ?? ''),
      message: String(formData.get('message') ?? ''),
    }

    const result = await onSubmitInquiry(payload)
    event.currentTarget.reset()
    setSubmitting(false)
    setStatus(
      result.storedInFirestore
        ? 'Message received. A follow-up response can be sent from the request inbox.'
        : 'Message was reviewed locally, but online submission is not active yet.',
    )
  }

  return (
    <section className="page-shell contact-grid">
      <SeoHead
        title="Contact Digital Signage Gear"
        description={data.pages.contact.summary}
        canonicalPath="/contact"
        structuredData={[
          buildBreadcrumbStructuredData([
            { name: 'Home', url: getSiteOrigin() },
            { name: 'Contact', url: toAbsoluteUrl('/contact') },
          ]),
        ]}
      />
      <div>
        <div className="section-heading align-left">
          <p className="eyebrow">Contact</p>
          <h1>{data.pages.contact.title}</h1>
          <p>{data.pages.contact.summary}</p>
        </div>
        <div className="faq-list compact-faq">
          {data.faqs.slice(0, 4).map((item) => (
            <details key={item.id} className="faq-item">
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
      <form className="contact-form" onSubmit={handleSubmit}>
        <label>
          Your name
          <input name="name" required />
        </label>
        <label>
          Your email
          <input name="email" type="email" required />
        </label>
        <label>
          Subject
          <input name="subject" required />
        </label>
        <label>
          Industry
          <select name="industry" defaultValue="Business">
            {data.homepage.industries.map((industry) => (
              <option key={industry.name} value={industry.name}>
                {industry.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Message
          <textarea name="message" rows={7} required />
        </label>
        <button type="submit" className="button button-primary" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
        {status ? <p className="status-note">{status}</p> : null}
      </form>
    </section>
  )
}

function AdminPage({
  data,
  onRefresh,
}: {
  data: CmsData
  onRefresh: () => Promise<void>
}) {
  const sortedProducts = [...data.products].sort((left, right) => left.title.localeCompare(right.title))
  const [email, setEmail] = useState<string | null>(null)
  const [uid, setUid] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [message, setMessage] = useState('')
  const [working, setWorking] = useState(false)
  const [credentialEmail, setCredentialEmail] = useState('')
  const [credentialPassword, setCredentialPassword] = useState('')
  const [checkoutRequests, setCheckoutRequests] = useState<AdminCheckoutRequest[]>([])
  const [inquiries, setInquiries] = useState<AdminInquiry[]>([])
  const [loadingRequests, setLoadingRequests] = useState(false)
  const [selectedDetail, setSelectedDetail] = useState<AdminDetailSelection>(null)
  const [selectedProductSlug, setSelectedProductSlug] = useState(sortedProducts[0]?.slug ?? '')
  const [productDraft, setProductDraft] = useState<ProductEditorDraft | null>(
    sortedProducts[0] ? createProductEditorDraft(sortedProducts[0]) : null,
  )
  const [mediaEntries, setMediaEntries] = useState<MediaManifestEntry[]>([])
  const [mediaQuery, setMediaQuery] = useState('')
  const [savingProduct, setSavingProduct] = useState(false)

  const selectedProduct =
    sortedProducts.find((product) => product.slug === selectedProductSlug) ?? sortedProducts[0] ?? null

  async function refreshRequests(cmsModule?: CmsModule) {
    setLoadingRequests(true)

    try {
      const cms = cmsModule ?? (await getCmsModule())
      const result = await cms.loadAdminRequests()
      const sortedCheckoutRequests = sortByCreatedAt(result.checkoutRequests)
      const sortedInquiries = sortByCreatedAt(result.inquiries)

      setCheckoutRequests(sortedCheckoutRequests)
      setInquiries(sortedInquiries)
      setSelectedDetail((current) => {
        if (current?.type === 'checkout') {
          return sortedCheckoutRequests.some((request) => request.id === current.id)
            ? current
            : sortedCheckoutRequests[0]
              ? { type: 'checkout', id: sortedCheckoutRequests[0].id }
              : sortedInquiries[0]
                ? { type: 'inquiry', id: sortedInquiries[0].id }
                : null
        }

        if (current?.type === 'inquiry') {
          return sortedInquiries.some((inquiry) => inquiry.id === current.id)
            ? current
            : sortedCheckoutRequests[0]
              ? { type: 'checkout', id: sortedCheckoutRequests[0].id }
              : sortedInquiries[0]
                ? { type: 'inquiry', id: sortedInquiries[0].id }
                : null
        }

        if (sortedCheckoutRequests[0]) {
          return { type: 'checkout', id: sortedCheckoutRequests[0].id }
        }

        if (sortedInquiries[0]) {
          return { type: 'inquiry', id: sortedInquiries[0].id }
        }

        return null
      })
    } finally {
      setLoadingRequests(false)
    }
  }

  const selectedCheckoutRequest =
    selectedDetail?.type === 'checkout'
      ? checkoutRequests.find((request) => request.id === selectedDetail.id) ?? null
      : null
  const selectedInquiry =
    selectedDetail?.type === 'inquiry'
      ? inquiries.find((inquiry) => inquiry.id === selectedDetail.id) ?? null
      : null

  const filteredMediaEntries = mediaEntries
    .filter((entry) => {
      const query = mediaQuery.trim().toLowerCase()

      if (!query) {
        return true
      }

      const haystack = `${entry.title ?? ''} ${entry.slug ?? ''} ${entry.localPath} ${entry.sourceUrl}`.toLowerCase()
      return haystack.includes(query)
    })
    .slice(0, 12)

  useEffect(() => {
    let cancelled = false
    let unsubscribe: () => void = () => undefined

    void getCmsModule().then((cms) => {
      if (cancelled) {
        return
      }

      unsubscribe = cms.watchCurrentUser(async (user) => {
        setEmail(user?.email ?? null)
        setUid(user?.uid ?? null)
        const adminState = await cms.ensureAdminStatus(user)
        setIsAdmin(adminState)

        if (adminState) {
          await refreshRequests(cms)
        } else {
          setCheckoutRequests([])
          setInquiries([])
          setSelectedDetail(null)
        }
      })
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    getCmsModule().then((cms) => cms.loadMediaManifestEntries()).then((entries) => {
      if (!cancelled) {
        setMediaEntries(entries)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  async function handleSignIn() {
    setWorking(true)
    setMessage('')
    try {
      const cms = await getCmsModule()
      const user = await cms.signInWithGoogle()
      setEmail(user.email ?? null)
      setUid(user.uid)
      setIsAdmin(await cms.ensureAdminStatus(user))
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to sign in.')
    } finally {
      setWorking(false)
    }
  }

  async function handleEmailSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setWorking(true)
    setMessage('')

    try {
      const cms = await getCmsModule()
      const user = await cms.signInWithEmailPassword(credentialEmail, credentialPassword)
      setEmail(user.email ?? null)
      setUid(user.uid)
      setIsAdmin(await cms.ensureAdminStatus(user))
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to sign in with email and password.')
    } finally {
      setWorking(false)
    }
  }

  async function handleSignOut() {
    const cms = await getCmsModule()
    await cms.signOutCurrentUser()
    setEmail(null)
    setUid(null)
    setIsAdmin(false)
  }

  async function handleSeed() {
    setWorking(true)
    setMessage('')

    try {
      const cms = await getCmsModule()
      await cms.seedFirestore(data)
      await onRefresh()
      setMessage('Catalog content refreshed successfully.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to refresh catalog content.')
    } finally {
      setWorking(false)
    }
  }

  async function handleCheckoutStatusChange(id: string, status: RequestStatus) {
    const cms = await getCmsModule()
    await cms.updateCheckoutRequestStatus(id, status)
    await refreshRequests()
    setMessage('Checkout request status updated.')
  }

  async function handleInquiryStatusChange(id: string, status: RequestStatus) {
    const cms = await getCmsModule()
    await cms.updateInquiryStatus(id, status)
    await refreshRequests()
    setMessage('Inquiry status updated.')
  }

  function handleSelectProduct(slug: string) {
    const nextProduct = sortedProducts.find((product) => product.slug === slug) ?? sortedProducts[0] ?? null

    setSelectedProductSlug(nextProduct?.slug ?? '')
    setProductDraft(nextProduct ? createProductEditorDraft(nextProduct) : null)
    setMediaQuery(nextProduct?.title ?? '')
  }

  async function handleProductSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedProduct || !productDraft) {
      return
    }

    const deploymentContexts = parseEditorList(productDraft.deploymentContexts)
    const hardwareTypes = parseEditorList(productDraft.hardwareTypes)
    const capabilities = parseEditorList(productDraft.capabilities)
    const nextCatalogGroup = productDraft.catalogGroup.trim() || selectedProduct.taxonomy.catalogGroup
    const nextProductFamily = productDraft.productFamily.trim() || selectedProduct.taxonomy.productFamily
    const priorTaxonomyValues = [
      selectedProduct.taxonomy.catalogGroup,
      selectedProduct.taxonomy.productFamily,
      ...selectedProduct.taxonomy.deploymentContexts,
    ]
    const persistentCategories = selectedProduct.categories.filter(
      (category) => !priorTaxonomyValues.includes(category),
    )

    setSavingProduct(true)
    setMessage('')

    try {
      const cms = await getCmsModule()
      await cms.updateProduct({
        ...selectedProduct,
        summary: productDraft.summary.trim(),
        image: productDraft.image.trim() || null,
        categories: uniqueStrings([
          nextCatalogGroup,
          nextProductFamily,
          ...deploymentContexts,
          ...persistentCategories,
        ]),
        taxonomy: {
          catalogGroup: nextCatalogGroup,
          productFamily: nextProductFamily,
          deploymentContexts,
          hardwareTypes,
          capabilities,
        },
      })
      await onRefresh()
      setMessage(`Updated ${selectedProduct.title} metadata.`)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update product metadata.')
    } finally {
      setSavingProduct(false)
    }
  }

  return (
    <section className="page-shell admin-shell">
      <SeoHead
        title="Admin"
        description="Digital Signage Gear admin workspace."
        canonicalPath="/admin"
        robots="noindex, nofollow"
      />
      <div className="section-heading align-left">
        <p className="eyebrow">Admin</p>
        <h1>Catalog and request operations.</h1>
        <p>
          Sign in with your admin account, confirm access, and manage catalog content,
          checkout handoffs, quote requests, and inbound inquiries.
        </p>
      </div>
      <div className="admin-card-grid">
        <article className="admin-card">
          <h2>Authentication</h2>
          <p>{email ?? 'No admin user signed in.'}</p>
          <p className="status-note">
            {isAdmin ? 'Admin access confirmed.' : 'Access is not enabled for this account yet.'}
          </p>
          <div className="hero-actions">
            <button type="button" className="button button-primary" onClick={handleSignIn} disabled={working}>
              Sign in with Google
            </button>
            <button type="button" className="button button-secondary" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
          <form className="admin-auth-form" onSubmit={handleEmailSignIn}>
            <label>
              Email
              <input
                type="email"
                value={credentialEmail}
                onChange={(event) => setCredentialEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={credentialPassword}
                onChange={(event) => setCredentialPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="button button-secondary" disabled={working}>
              Sign in with email/password
            </button>
          </form>
        </article>
        <article className="admin-card">
          <h2>Access Setup</h2>
          <p>Use this UID when granting admin access for content and request management.</p>
          <p className="uid-pill">{uid ?? 'Sign in first'}</p>
          <button type="button" className="button button-primary" onClick={handleSeed} disabled={!isAdmin || working}>
            Refresh Catalog Content
          </button>
        </article>
        <article className="admin-card">
          <h2>Content Overview</h2>
          <ul className="admin-list">
            <li>{data.products.length} products</li>
            <li>{data.posts.length} blog posts</li>
            <li>{data.faqs.length} FAQs</li>
            <li>{Object.keys(data.pages).length} primary content pages</li>
          </ul>
        </article>
      </div>
      <section className="admin-request-section">
        <div className="section-heading align-left">
          <p className="eyebrow">Request Inbox</p>
          <h2>Orders, quotes, and contact requests.</h2>
          <p>
            Use this inbox to review incoming request traffic and move each item through
            your sales and invoicing workflow.
          </p>
        </div>
        {isAdmin ? (
          <div className="admin-request-grid">
            <div className="admin-request-column">
              <div className="admin-request-group">
                <div className="admin-request-header-row">
                  <h3>Checkout and Quote Requests</h3>
                  <button type="button" className="button button-secondary" onClick={() => void refreshRequests()} disabled={loadingRequests}>
                    {loadingRequests ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
                <div className="admin-request-list">
                  {checkoutRequests.length ? (
                    checkoutRequests.map((request) => {
                      const isSelected =
                        selectedDetail?.type === 'checkout' && selectedDetail.id === request.id

                      return (
                        <article
                          key={request.id}
                          className={isSelected ? 'admin-request-card admin-request-card-active' : 'admin-request-card'}
                        >
                          <div className="admin-request-topline">
                            <strong>{request.requestNumber}</strong>
                            <span className={`status-pill status-${request.status}`}>{request.status}</span>
                          </div>
                          <h3>{request.requestType === 'quote' ? 'Quote Request' : 'Checkout Handoff'}</h3>
                          <p>{request.name} · {request.email}</p>
                          <p>{request.company || 'No company listed'}</p>
                          <p>{request.items.length} items · ${request.total.toFixed(2)}</p>
                          <div className="admin-request-actions-row">
                            <button
                              type="button"
                              className="button button-secondary"
                              onClick={() => setSelectedDetail({ type: 'checkout', id: request.id })}
                            >
                              View Details
                            </button>
                            <label className="status-select-row compact-status-select-row">
                              <span>Status</span>
                              <select
                                value={request.status}
                                onChange={(event) => void handleCheckoutStatusChange(request.id, event.target.value as RequestStatus)}
                              >
                                {requestStatuses.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </article>
                      )
                    })
                  ) : (
                    <div className="cart-empty">No checkout or quote requests yet.</div>
                  )}
                </div>
              </div>
              <div className="admin-request-group">
                <h3>Contact Inquiries</h3>
                <div className="admin-request-list">
                  {inquiries.length ? (
                    inquiries.map((inquiry) => {
                      const isSelected =
                        selectedDetail?.type === 'inquiry' && selectedDetail.id === inquiry.id

                      return (
                        <article
                          key={inquiry.id}
                          className={isSelected ? 'admin-request-card admin-request-card-active' : 'admin-request-card'}
                        >
                          <div className="admin-request-topline">
                            <strong>{inquiry.subject}</strong>
                            <span className={`status-pill status-${inquiry.status ?? 'new'}`}>{inquiry.status ?? 'new'}</span>
                          </div>
                          <p>{inquiry.name} · {inquiry.email}</p>
                          <p>{inquiry.industry}</p>
                          <div className="admin-request-actions-row">
                            <button
                              type="button"
                              className="button button-secondary"
                              onClick={() => setSelectedDetail({ type: 'inquiry', id: inquiry.id })}
                            >
                              View Details
                            </button>
                            <label className="status-select-row compact-status-select-row">
                              <span>Status</span>
                              <select
                                value={inquiry.status ?? 'new'}
                                onChange={(event) => void handleInquiryStatusChange(inquiry.id, event.target.value as RequestStatus)}
                              >
                                {requestStatuses.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </article>
                      )
                    })
                  ) : (
                    <div className="cart-empty">No contact inquiries yet.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="admin-request-detail-shell">
              {selectedCheckoutRequest ? (
                <CheckoutRequestDetail
                  request={selectedCheckoutRequest}
                  onUpdateStatus={handleCheckoutStatusChange}
                />
              ) : null}
              {selectedInquiry ? (
                <InquiryDetail inquiry={selectedInquiry} onUpdateStatus={handleInquiryStatusChange} />
              ) : null}
              {!selectedCheckoutRequest && !selectedInquiry ? (
                <div className="cart-empty">Select a request to view the full detail panel.</div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="cart-empty">Sign in as an admin to view the request inbox.</div>
        )}
      </section>
      <section className="admin-request-section">
        <div className="section-heading align-left">
          <p className="eyebrow">Catalog Editor</p>
          <h2>Update product taxonomy and imagery.</h2>
          <p>
            This first-pass editor updates the live Firestore product metadata for imagery,
            summary copy, and taxonomy fields without reseeding the full catalog.
          </p>
        </div>
        {isAdmin && selectedProduct && productDraft ? (
          <article className="admin-request-detail-card admin-product-editor-shell">
            <div className="admin-product-toolbar">
              <label className="status-select-row">
                Product
                <select
                  value={selectedProduct.slug}
                  onChange={(event) => handleSelectProduct(event.target.value)}
                  disabled={savingProduct}
                >
                  {sortedProducts.map((product) => (
                    <option key={product.slug} value={product.slug}>
                      {product.title}
                    </option>
                  ))}
                </select>
              </label>
              <div>
                <p className="filter-label">Selected slug</p>
                <p className="status-note">{selectedProduct.slug}</p>
              </div>
            </div>
            <form className="admin-product-form" onSubmit={handleProductSave}>
              <div className="admin-product-grid">
                <div className="admin-product-preview-card">
                  {productDraft.image ? (
                    <img
                      className="admin-product-preview"
                      src={productDraft.image}
                      alt={selectedProduct.title}
                    />
                  ) : (
                    <div className="product-detail-placeholder">No image assigned</div>
                  )}
                  <label>
                    Image path
                    <input
                      value={productDraft.image}
                      list="wordpress-media-options"
                      onChange={(event) =>
                        setProductDraft((current) =>
                          current ? { ...current, image: event.target.value } : current,
                        )
                      }
                      placeholder="/wordpress-media/example.jpg"
                    />
                  </label>
                  <datalist id="wordpress-media-options">
                    {mediaEntries.map((entry) => (
                      <option key={entry.localPath} value={entry.localPath}>
                        {entry.title ?? entry.slug ?? entry.localPath}
                      </option>
                    ))}
                  </datalist>
                  <label>
                    Browse WordPress media
                    <input
                      value={mediaQuery}
                      onChange={(event) => setMediaQuery(event.target.value)}
                      placeholder="Search menu, kiosk, hospital, lobby..."
                    />
                  </label>
                  <div className="admin-media-picker-list" aria-label="WordPress media options">
                    {filteredMediaEntries.map((entry) => (
                      <button
                        key={entry.localPath}
                        type="button"
                        className={productDraft.image === entry.localPath ? 'admin-media-option admin-media-option-active' : 'admin-media-option'}
                        onClick={() =>
                          setProductDraft((current) =>
                            current ? { ...current, image: entry.localPath } : current,
                          )
                        }
                      >
                        <img src={entry.localPath} alt={entry.title ?? entry.slug ?? 'WordPress media option'} />
                        <span>{entry.title ?? entry.slug ?? entry.localPath}</span>
                      </button>
                    ))}
                  </div>
                  <p className="status-note">
                    Pick from the imported WordPress asset library or paste a local asset path.
                  </p>
                </div>
                <div className="admin-product-fields">
                  <label>
                    Summary
                    <textarea
                      rows={4}
                      value={productDraft.summary}
                      onChange={(event) =>
                        setProductDraft((current) =>
                          current ? { ...current, summary: event.target.value } : current,
                        )
                      }
                    />
                  </label>
                  <div className="admin-product-meta-grid">
                    <label>
                      Catalog group
                      <input
                        value={productDraft.catalogGroup}
                        onChange={(event) =>
                          setProductDraft((current) =>
                            current ? { ...current, catalogGroup: event.target.value } : current,
                          )
                        }
                      />
                    </label>
                    <label>
                      Product family
                      <input
                        value={productDraft.productFamily}
                        onChange={(event) =>
                          setProductDraft((current) =>
                            current ? { ...current, productFamily: event.target.value } : current,
                          )
                        }
                      />
                    </label>
                  </div>
                  <label>
                    Deployment contexts
                    <input
                      value={productDraft.deploymentContexts}
                      onChange={(event) =>
                        setProductDraft((current) =>
                          current ? { ...current, deploymentContexts: event.target.value } : current,
                        )
                      }
                      placeholder="Business, Retail, Lobbies"
                    />
                  </label>
                  <label>
                    Hardware types
                    <input
                      value={productDraft.hardwareTypes}
                      onChange={(event) =>
                        setProductDraft((current) =>
                          current ? { ...current, hardwareTypes: event.target.value } : current,
                        )
                      }
                      placeholder="Display, Media Player"
                    />
                  </label>
                  <label>
                    Capabilities
                    <input
                      value={productDraft.capabilities}
                      onChange={(event) =>
                        setProductDraft((current) =>
                          current ? { ...current, capabilities: event.target.value } : current,
                        )
                      }
                      placeholder="4K, Scheduling, Touch-Ready"
                    />
                  </label>
                </div>
              </div>
              <div className="hero-actions">
                <button type="submit" className="button button-primary" disabled={savingProduct}>
                  {savingProduct ? 'Saving...' : 'Save Product Metadata'}
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={savingProduct}
                  onClick={() => setProductDraft(createProductEditorDraft(selectedProduct))}
                >
                  Reset Changes
                </button>
              </div>
            </form>
          </article>
        ) : (
          <div className="cart-empty">Sign in as an admin to edit product taxonomy and imagery.</div>
        )}
      </section>
      {message ? <p className="status-note">{message}</p> : null}
    </section>
  )
}

function CheckoutRequestDetail({
  request,
  onUpdateStatus,
}: {
  request: AdminCheckoutRequest
  onUpdateStatus: (id: string, status: RequestStatus) => Promise<void>
}) {
  return (
    <article className="admin-request-detail-card">
      <div className="admin-request-topline">
        <div>
          <p className="eyebrow">Request Detail</p>
          <h3>{request.requestNumber}</h3>
        </div>
        <span className={`status-pill status-${request.status}`}>{request.status}</span>
      </div>
      <div className="admin-detail-toolbar">
        <button
          type="button"
          className="button button-primary"
          onClick={() => downloadInvoiceDocument(request)}
        >
          Export Invoice HTML
        </button>
        <button
          type="button"
          className="button button-secondary"
          onClick={() => printInvoiceDocument(request)}
        >
          Print Invoice Draft
        </button>
      </div>
      <label className="status-select-row">
        Status
        <select
          value={request.status}
          onChange={(event) => void onUpdateStatus(request.id, event.target.value as RequestStatus)}
        >
          {requestStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <div className="admin-detail-grid">
        <div className="admin-detail-section">
          <h4>Customer</h4>
          <p>{request.name}</p>
          <p>{request.email}</p>
          <p>{request.phone || 'No phone provided'}</p>
          <p>{request.company || 'No company provided'}</p>
          <p>Preferred contact: {request.preferredContactMethod || 'Not specified'}</p>
        </div>
        <div className="admin-detail-section">
          <h4>Fulfillment</h4>
          <p>Type: {request.requestType === 'quote' ? 'Quote request' : 'Checkout handoff'}</p>
          <p>PO: {request.poNumber || 'Not provided'}</p>
          <p>Requested delivery: {request.requestedDeliveryDate || 'Not provided'}</p>
          <p>Submitted: {formatAdminTimestamp(request.createdAt)}</p>
          <p>Invoice draft: {getInvoiceNumber(request)}</p>
        </div>
      </div>
      <div className="admin-detail-section">
        <h4>Billing Address</h4>
        <p className="admin-detail-prewrap">{request.billingAddress || 'No billing address provided.'}</p>
      </div>
      <div className="admin-detail-section">
        <h4>Shipping Address</h4>
        <p className="admin-detail-prewrap">{request.shippingAddress || 'No shipping address provided.'}</p>
      </div>
      <div className="admin-detail-section">
        <h4>Line Items</h4>
        <div className="invoice-line-list">
          {request.items.map((item) => (
            <div key={item.slug} className="invoice-line-row">
              <div>
                <strong>{item.title}</strong>
                <p>{item.slug}</p>
              </div>
              <div className="invoice-line-totals">
                <span>Qty {item.quantity}</span>
                <strong>
                  {item.price ? `$${(item.price * item.quantity).toFixed(2)}` : 'Request pricing'}
                </strong>
              </div>
            </div>
          ))}
        </div>
        <div className="cart-summary">
          <strong>Total</strong>
          <span>${request.total.toFixed(2)}</span>
        </div>
      </div>
      <div className="admin-detail-section">
        <h4>Notes</h4>
        <p className="admin-detail-prewrap">{request.notes || 'No notes provided.'}</p>
      </div>
    </article>
  )
}

function InquiryDetail({
  inquiry,
  onUpdateStatus,
}: {
  inquiry: AdminInquiry
  onUpdateStatus: (id: string, status: RequestStatus) => Promise<void>
}) {
  const status = inquiry.status ?? 'new'

  return (
    <article className="admin-request-detail-card">
      <div className="admin-request-topline">
        <div>
          <p className="eyebrow">Inquiry Detail</p>
          <h3>{inquiry.subject}</h3>
        </div>
        <span className={`status-pill status-${status}`}>{status}</span>
      </div>
      <label className="status-select-row">
        Status
        <select
          value={status}
          onChange={(event) => void onUpdateStatus(inquiry.id, event.target.value as RequestStatus)}
        >
          {requestStatuses.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <div className="admin-detail-grid">
        <div className="admin-detail-section">
          <h4>Sender</h4>
          <p>{inquiry.name}</p>
          <p>{inquiry.email}</p>
          <p>{inquiry.industry}</p>
        </div>
        <div className="admin-detail-section">
          <h4>Timeline</h4>
          <p>Submitted: {formatAdminTimestamp(inquiry.createdAt)}</p>
          <p>Subject: {inquiry.subject}</p>
        </div>
      </div>
      <div className="admin-detail-section">
        <h4>Message</h4>
        <p className="admin-detail-prewrap">{inquiry.message}</p>
      </div>
    </article>
  )
}

function CartPage({
  items,
  onUpdateQuantity,
  onRemove,
}: {
  items: CartItem[]
  onUpdateQuantity: (slug: string, quantity: number) => void
  onRemove: (slug: string) => void
}) {
  const total = items.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0)

  return (
    <section className="page-shell">
      <SeoHead
        title="Cart"
        description="Review your selected digital signage items before checkout or requesting a quote."
        canonicalPath="/cart"
        robots="noindex, nofollow"
      />
      <div className="section-heading align-left">
        <p className="eyebrow">Cart</p>
        <h1>Your selected signage gear.</h1>
        <p>Review your selections, then continue to checkout or request a formal quote.</p>
      </div>
      <div className="cart-list">
        {items.length ? (
          items.map((item) => (
            <article key={item.slug} className="cart-item">
              {item.image ? <img className="cart-item-image" src={item.image} alt={item.title} loading="lazy" decoding="async" /> : null}
              <div className="cart-item-copy">
                <h2>{item.title}</h2>
                <p>{item.price ? `$${item.price.toFixed(2)}` : 'Request pricing'}</p>
              </div>
              <label className="cart-quantity">
                Qty
                <input
                  type="number"
                  min={0}
                  value={item.quantity}
                  onChange={(event) =>
                    onUpdateQuantity(item.slug, Math.max(0, Number(event.target.value) || 0))
                  }
                />
              </label>
              <button type="button" className="button button-secondary" onClick={() => onRemove(item.slug)}>
                Remove
              </button>
            </article>
          ))
        ) : (
          <div className="cart-empty">Your cart is empty.</div>
        )}
      </div>
      <div className="cart-summary">
        <strong>Total</strong>
        <span>${total.toFixed(2)}</span>
      </div>
      <div className="cart-actions">
        <NavLink to="/checkout?mode=checkout" className="button button-primary">
          Continue to Checkout
        </NavLink>
        <NavLink to="/checkout?mode=quote" className="button button-secondary">
          Request a Quote
        </NavLink>
      </div>
    </section>
  )
}

function CheckoutPage({
  items,
  onSubmitRequest,
  onClearCart,
}: {
  items: CartItem[]
  onSubmitRequest: (payload: CheckoutRequestPayload) => Promise<{ storedInFirestore: boolean }>
  onClearCart: () => void
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const requestType = params.get('mode') === 'quote' ? 'quote' : 'checkout'
  const [status, setStatus] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const total = items.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setStatus('')

    const formData = new FormData(event.currentTarget)
    const requestNumber = `${requestType === 'quote' ? 'Q' : 'C'}-${Date.now().toString().slice(-6)}`
    const result = await onSubmitRequest({
      requestType,
      requestNumber,
      status: 'new',
      name: String(formData.get('name') ?? ''),
      email: String(formData.get('email') ?? ''),
      company: String(formData.get('company') ?? ''),
      phone: String(formData.get('phone') ?? ''),
      billingAddress: String(formData.get('billingAddress') ?? ''),
      shippingAddress: String(formData.get('shippingAddress') ?? ''),
      poNumber: String(formData.get('poNumber') ?? ''),
      requestedDeliveryDate: String(formData.get('requestedDeliveryDate') ?? ''),
      preferredContactMethod: String(formData.get('preferredContactMethod') ?? ''),
      notes: String(formData.get('notes') ?? ''),
      items: items.map((item) => ({
        slug: item.slug,
        title: item.title,
        price: item.price,
        quantity: item.quantity,
      })),
      total,
    })

    setSubmitting(false)

    if (result.storedInFirestore) {
      setStatus(
        requestType === 'quote'
          ? 'Quote request submitted successfully.'
          : 'Checkout handoff submitted successfully.',
      )
      event.currentTarget.reset()
      onClearCart()
      return
    }

    setStatus('Request was reviewed locally, but online submission is not active yet.')
  }

  return (
    <section className="page-shell checkout-shell">
      <SeoHead
        title={requestType === 'quote' ? 'Request a Quote' : 'Checkout Handoff'}
        description={
          requestType === 'quote'
            ? 'Request pricing, recommendations, and deployment guidance for your selected digital signage products.'
            : 'Send your selected digital signage items to the sales team to continue the purchasing process.'
        }
        canonicalPath="/checkout"
        robots="noindex, nofollow"
      />
      <div className="section-heading align-left">
        <p className="eyebrow">{requestType === 'quote' ? 'Quote Request' : 'Checkout Handoff'}</p>
        <h1>{requestType === 'quote' ? 'Request a formal quote.' : 'Send your cart to the sales team.'}</h1>
        <p>
          {requestType === 'quote'
            ? 'Tell us a bit about your deployment and we will respond with pricing and recommendations.'
            : 'Submit your selected items and contact details to continue the purchasing conversation.'}
        </p>
      </div>
      <div className="checkout-grid">
        <form className="contact-form checkout-form" onSubmit={handleSubmit}>
          <div className="request-type-row">
            <button
              type="button"
              className={requestType === 'checkout' ? 'chip chip-active' : 'chip'}
              onClick={() => navigate('/checkout?mode=checkout')}
            >
              Checkout Handoff
            </button>
            <button
              type="button"
              className={requestType === 'quote' ? 'chip chip-active' : 'chip'}
              onClick={() => navigate('/checkout?mode=quote')}
            >
              Quote Request
            </button>
          </div>
          <label>
            Name
            <input name="name" required />
          </label>
          <label>
            Email
            <input name="email" type="email" required />
          </label>
          <label>
            Company
            <input name="company" />
          </label>
          <label>
            Phone
            <input name="phone" />
          </label>
          <label>
            Billing Address
            <textarea name="billingAddress" rows={4} />
          </label>
          <label>
            Shipping Address
            <textarea name="shippingAddress" rows={4} />
          </label>
          <label>
            PO Number
            <input name="poNumber" />
          </label>
          <label>
            Requested Delivery Date
            <input name="requestedDeliveryDate" type="date" />
          </label>
          <label>
            Preferred Contact Method
            <select name="preferredContactMethod" defaultValue="email">
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
          </label>
          <label>
            Notes
            <textarea
              name="notes"
              rows={7}
              placeholder="Tell us about installation timing, locations, preferred brands, or anything else useful."
            />
          </label>
          <button type="submit" className="button button-primary" disabled={!items.length || submitting}>
            {submitting
              ? 'Submitting...'
              : requestType === 'quote'
                ? 'Submit Quote Request'
                : 'Submit Checkout Handoff'}
          </button>
          {status ? <p className="status-note">{status}</p> : null}
        </form>
        <div className="checkout-summary">
          <h2>Selected Items</h2>
          <p className="checkout-summary-copy">
            {requestType === 'quote'
              ? 'Use a quote request when you want pricing guidance, kit recommendations, or a deployment conversation before committing.'
              : 'Use checkout handoff when you already know the items you want and need Digital Signage Gear to continue the order and invoicing process.'}
          </p>
          <div className="checkout-item-list">
            {items.length ? (
              items.map((item) => (
                <article key={item.slug} className="checkout-item">
                  {item.image ? <img className="checkout-item-image" src={item.image} alt={item.title} loading="lazy" decoding="async" /> : null}
                  <div>
                    <h3>{item.title}</h3>
                    <p>
                      Qty {item.quantity}
                      {item.price ? ` · $${item.price.toFixed(2)}` : ' · Request pricing'}
                    </p>
                  </div>
                </article>
              ))
            ) : (
              <div className="cart-empty">Your cart is empty.</div>
            )}
          </div>
          <div className="cart-summary">
            <strong>Total</strong>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function sortByCreatedAt<T extends { createdAt?: unknown }>(items: T[]) {
  return [...items].sort(
    (left, right) => getTimestampValue(right.createdAt) - getTimestampValue(left.createdAt),
  )
}

function getTimestampValue(value: unknown) {
  if (!value || typeof value !== 'object') {
    return 0
  }

  if ('toMillis' in value && typeof value.toMillis === 'function') {
    return value.toMillis()
  }

  if ('seconds' in value && typeof value.seconds === 'number') {
    return value.seconds * 1000
  }

  return 0
}

function formatAdminTimestamp(value: unknown) {
  const timestampValue = getTimestampValue(value)

  if (!timestampValue) {
    return 'Not available'
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestampValue)
}

function getInvoiceNumber(request: AdminCheckoutRequest) {
  return `INV-${request.requestNumber}`
}

function downloadInvoiceDocument(request: AdminCheckoutRequest) {
  const html = createInvoiceHtml(request)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = `${getInvoiceNumber(request).toLowerCase()}.html`
  link.click()
  URL.revokeObjectURL(url)
}

function printInvoiceDocument(request: AdminCheckoutRequest) {
  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1100,height=850')

  if (!printWindow) {
    return
  }

  printWindow.document.write(createInvoiceHtml(request))
  printWindow.document.close()
  printWindow.focus()
  printWindow.print()
}

function createInvoiceHtml(request: AdminCheckoutRequest) {
  const lineItems = request.items
    .map(
      (item) => `
        <tr>
          <td>${escapeHtml(item.title)}</td>
          <td>${escapeHtml(item.slug)}</td>
          <td>${item.quantity}</td>
          <td>${item.price ? `$${item.price.toFixed(2)}` : 'Request pricing'}</td>
          <td>${item.price ? `$${(item.price * item.quantity).toFixed(2)}` : 'Request pricing'}</td>
        </tr>
      `,
    )
    .join('')

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(getInvoiceNumber(request))}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 32px; color: #1f1533; }
      header { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 28px; }
      h1, h2, h3, p { margin: 0; }
      h1 { font-size: 28px; margin-bottom: 8px; }
      .meta, .addresses { display: grid; gap: 16px; margin-bottom: 24px; }
      .addresses { grid-template-columns: 1fr 1fr; }
      .card { border: 1px solid #dbcff3; border-radius: 16px; padding: 16px; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border-bottom: 1px solid #e8def8; text-align: left; padding: 12px 10px; }
      th { font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; color: #73579c; }
      .total { margin-top: 20px; text-align: right; font-size: 22px; font-weight: bold; }
      .notes { white-space: pre-wrap; }
    </style>
  </head>
  <body>
    <header>
      <div>
        <h1>Digital Signage Gear</h1>
        <p>Invoice Draft</p>
      </div>
      <div>
        <p><strong>${escapeHtml(getInvoiceNumber(request))}</strong></p>
        <p>${escapeHtml(request.requestNumber)}</p>
        <p>${escapeHtml(formatAdminTimestamp(request.createdAt))}</p>
      </div>
    </header>
    <section class="meta">
      <div class="card">
        <h3>Customer</h3>
        <p>${escapeHtml(request.name)}</p>
        <p>${escapeHtml(request.company || 'No company provided')}</p>
        <p>${escapeHtml(request.email)}</p>
        <p>${escapeHtml(request.phone || 'No phone provided')}</p>
      </div>
    </section>
    <section class="addresses">
      <div class="card">
        <h3>Billing Address</h3>
        <p class="notes">${escapeHtml(request.billingAddress || 'No billing address provided.')}</p>
      </div>
      <div class="card">
        <h3>Shipping Address</h3>
        <p class="notes">${escapeHtml(request.shippingAddress || 'No shipping address provided.')}</p>
      </div>
    </section>
    <section class="card">
      <h3>Order Summary</h3>
      <p>Type: ${escapeHtml(request.requestType === 'quote' ? 'Quote request' : 'Checkout handoff')}</p>
      <p>PO Number: ${escapeHtml(request.poNumber || 'Not provided')}</p>
      <p>Requested Delivery: ${escapeHtml(request.requestedDeliveryDate || 'Not provided')}</p>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>SKU</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Line Total</th>
          </tr>
        </thead>
        <tbody>${lineItems}</tbody>
      </table>
      <div class="total">Total: $${request.total.toFixed(2)}</div>
    </section>
    <section class="card" style="margin-top: 24px;">
      <h3>Notes</h3>
      <p class="notes">${escapeHtml(request.notes || 'No notes provided.')}</p>
    </section>
  </body>
</html>`
}

function escapeHtml(input: string) {
  return input
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function NotFoundPage() {
  return (
    <section className="page-shell not-found-shell">
      <SeoHead
        title="Page Not Found"
        description="The requested page could not be found on Digital Signage Gear."
        canonicalPath="/404"
        robots="noindex, nofollow"
      />
      <p className="eyebrow">404</p>
      <h1>That page could not be found.</h1>
      <NavLink to="/" className="button button-primary">
        Return home
      </NavLink>
    </section>
  )
}

function formatDate(input: string) {
  const date = new Date(input)

  if (Number.isNaN(date.valueOf())) {
    return input
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export default App
